// Minimal Markdown -> HTML renderer for natively-authored SOPs.
//
// Written by hand rather than pulled from npm on purpose: the client has no
// markdown dependency today, and adding one would force a `npm run build`,
// which on this deployment ships straight to production.
//
// SAFETY: the input is HTML-escaped in full BEFORE any markdown rule runs, and
// every tag in the output is one this file emits. User content can therefore
// never introduce an element or attribute, which is what makes the result safe
// to hand to dangerouslySetInnerHTML. The only attribute built from user text
// is a link href, and that is scheme-checked below.
//
// Supported: headings, bold, italic, inline code, fenced code, links, images,
// ordered/unordered lists (one level of nesting), blockquotes, tables,
// horizontal rules, paragraphs.

const escapeHtml = (str) =>
  String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

// Anything that isn't plainly http(s), mailto, or a same-site relative path is
// dropped — this is what stops `[click](javascript:...)` from becoming live.
const safeUrl = (url) => {
  const trimmed = String(url || '').trim();
  if (/^(https?:\/\/|mailto:|\/|#)/i.test(trimmed)) return trimmed;
  return '';
};

// Sentinel wrapping extracted inline-code spans while the other inline rules
// run. NUL cannot survive escapeHtml from real input, so it can never collide
// with user content.
const CODE_MARK = '\u0000';
const CODE_PLACEHOLDER = new RegExp(`${CODE_MARK}(\\d+)${CODE_MARK}`, 'g');

/** Inline rules, applied to already-escaped text. */
const inline = (text) => {
  // Pull inline code out first so its contents are not further transformed —
  // otherwise `**not bold**` inside backticks would still be bolded.
  const codes = [];
  let out = text.replace(/`([^`]+)`/g, (_, code) => {
    codes.push(code);
    return `${CODE_MARK}${codes.length - 1}${CODE_MARK}`;
  });

  out = out
    // ![alt](src)
    .replace(/!\[([^\]]*)\]\(([^)\s]+)\)/g, (m, alt, src) => {
      const href = safeUrl(src);
      return href ? `<img src="${href}" alt="${alt}" class="max-w-full rounded-md my-2" />` : alt;
    })
    // [text](href)
    .replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (m, label, href) => {
      const url = safeUrl(href);
      return url
        ? `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-indigo-400 hover:text-indigo-300 underline">${label}</a>`
        : label;
    })
    .replace(/\*\*([^*]+)\*\*/g, '<strong class="font-semibold text-slate-100">$1</strong>')
    .replace(/(^|[^*])\*([^*\n]+)\*/g, '$1<em>$2</em>')
    .replace(/~~([^~]+)~~/g, '<del class="text-slate-400">$1</del>');

  return out.replace(CODE_PLACEHOLDER, (_, i) =>
    `<code class="px-1.5 py-0.5 rounded bg-slate-900 text-indigo-300 text-[0.9em] font-mono">${codes[Number(i)]}</code>`);
};

const CODE_BLOCK_OPEN =
  '<pre class="my-3 p-3 rounded-md bg-slate-900 border border-slate-700 overflow-x-auto">' +
  '<code class="text-sm font-mono text-slate-200">';

const renderTable = (rows) => {
  const cells = (line) => line.replace(/^\||\|$/g, '').split('|').map((c) => c.trim());

  const header = cells(rows[0]);
  const bodyRows = rows.slice(2).map(cells); // rows[1] is the --- separator

  const head = header
    .map((c) => `<th class="px-3 py-2 text-left font-semibold text-slate-200 border-b border-slate-600">${inline(c)}</th>`)
    .join('');
  const body = bodyRows
    .map((r) => `<tr class="border-b border-slate-700/60">${r
      .map((c) => `<td class="px-3 py-2 align-top text-slate-300">${inline(c)}</td>`)
      .join('')}</tr>`)
    .join('');

  return `<div class="overflow-x-auto my-4"><table class="min-w-full text-sm border border-slate-700 rounded-md">
    <thead class="bg-slate-800">${head ? `<tr>${head}</tr>` : ''}</thead><tbody>${body}</tbody></table></div>`;
};

export const renderMarkdown = (source) => {
  if (!source) return '';

  const lines = escapeHtml(source).replace(/\r\n?/g, '\n').split('\n');
  const html = [];

  let listType = null;      // 'ul' | 'ol' | null
  let inCodeBlock = false;
  let codeBuffer = [];
  let paragraph = [];
  let quote = [];

  const flushCode = () => {
    html.push(`${CODE_BLOCK_OPEN}${codeBuffer.join('\n')}</code></pre>`);
    codeBuffer = [];
  };
  const closeList = () => { if (listType) { html.push(`</${listType}>`); listType = null; } };
  const closeParagraph = () => {
    if (paragraph.length) {
      html.push(`<p class="my-3 leading-relaxed text-slate-300">${inline(paragraph.join(' '))}</p>`);
      paragraph = [];
    }
  };
  const closeQuote = () => {
    if (quote.length) {
      html.push(`<blockquote class="border-l-4 border-indigo-500/60 bg-slate-800/40 pl-4 py-2 my-3 text-slate-300 italic">${inline(quote.join(' '))}</blockquote>`);
      quote = [];
    }
  };
  const closeAll = () => { closeParagraph(); closeQuote(); closeList(); };

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // --- fenced code ---
    if (/^\s*```/.test(line)) {
      if (inCodeBlock) { flushCode(); inCodeBlock = false; } else { closeAll(); inCodeBlock = true; }
      continue;
    }
    if (inCodeBlock) { codeBuffer.push(line); continue; }

    // --- table (header row + separator row + body) ---
    if (/^\s*\|.*\|\s*$/.test(line) && i + 1 < lines.length && /^\s*\|[\s:|-]+\|\s*$/.test(lines[i + 1])) {
      closeAll();
      const block = [];
      while (i < lines.length && /^\s*\|.*\|\s*$/.test(lines[i])) { block.push(lines[i].trim()); i += 1; }
      i -= 1;
      html.push(renderTable(block));
      continue;
    }

    // --- blank line ends any open block ---
    if (!line.trim()) { closeAll(); continue; }

    // --- horizontal rule ---
    if (/^\s*([-*_])\s*\1\s*\1[\s\-*_]*$/.test(line)) {
      closeAll();
      html.push('<hr class="my-5 border-slate-700" />');
      continue;
    }

    // --- headings ---
    const heading = /^(#{1,6})\s+(.*)$/.exec(line);
    if (heading) {
      closeAll();
      const level = heading[1].length;
      const sizes = {
        1: 'text-2xl font-bold text-slate-100 mt-6 mb-3',
        2: 'text-xl font-bold text-slate-100 mt-5 mb-2',
        3: 'text-lg font-semibold text-slate-100 mt-4 mb-2',
        4: 'text-base font-semibold text-slate-200 mt-3 mb-1',
        5: 'text-sm font-semibold text-slate-200 mt-3 mb-1',
        6: 'text-sm font-semibold text-slate-400 mt-3 mb-1',
      };
      html.push(`<h${level} class="${sizes[level]}">${inline(heading[2])}</h${level}>`);
      continue;
    }

    // --- blockquote ---
    // Matches &gt; because escapeHtml has already run over the whole document
    // by this point; a literal '>' never survives to reach the block rules.
    const quoted = /^\s*(?:&gt;|>)\s?(.*)$/.exec(line);
    if (quoted) { closeParagraph(); closeList(); quote.push(quoted[1]); continue; }

    // --- lists ---
    const unordered = /^(\s*)[-*+]\s+(.*)$/.exec(line);
    const ordered = /^(\s*)\d+[.)]\s+(.*)$/.exec(line);
    const listMatch = unordered || ordered;
    if (listMatch) {
      closeParagraph();
      closeQuote();
      const wanted = unordered ? 'ul' : 'ol';
      if (listType && listType !== wanted) closeList();
      if (!listType) {
        listType = wanted;
        const style = wanted === 'ul' ? 'list-disc' : 'list-decimal';
        html.push(`<${wanted} class="${style} pl-6 my-3 space-y-1 text-slate-300 marker:text-indigo-400">`);
      }
      html.push(`<li class="${listMatch[1].length >= 2 ? 'ml-4' : ''} leading-relaxed">${inline(listMatch[2])}</li>`);
      continue;
    }

    closeList();
    closeQuote();
    paragraph.push(line.trim());
  }

  // An unterminated fence still renders rather than swallowing the rest.
  if (inCodeBlock && codeBuffer.length) flushCode();
  closeAll();

  return html.join('\n');
};

/** Plain-text preview for cards and search snippets. */
export const markdownToPlain = (source, maxLength = 220) => {
  if (!source) return '';
  const text = String(source)
    .replace(/```[\s\S]*?```/g, ' ')
    .replace(/!\[[^\]]*\]\([^)]*\)/g, ' ')
    .replace(/\[([^\]]+)\]\([^)]*\)/g, '$1')
    .replace(/[#>*_~`|-]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
};

export default renderMarkdown;
